/**
 * 数据主权区 —— **N 域 N4 的 UI 入口**（Web 设置页）
 *
 * 依据：N 域 PRD **§5.4.1**（导出两档制）· **§7.4 `AC-N4.5`–`AC-N4.13`**（服务端已落地）·
 * **`AC-N4.11`**（🔴 **排除表在 UI 逐条明示 + 给出原因**）· 原型 `08-家庭与设置_原型.html` 面板 3 ·
 * **§6.4 铁律 3**（🔴 **导出能力实现唯一在 C 域流水侧；N 域只放入口**）。
 *
 * ## 🔴 本组件是"入口"，不是"实现"
 *
 * 两个按钮各自打一个**已存在**的服务端接口：
 * | 按钮 | 接口 | 谁实现 |
 * |---|---|---|
 * | 导出 CSV | `GET /api/transactions/export?exportAll=true&format=csv` | **C 域**（`TransactionExportService`） |
 * | 导出 JSON | `GET /api/settings/backup` | **N 域**（`BackupService`，批次 `61`） |
 *
 * ⇒ 本文件**一行口径都不实现**：不拼日期（文件名解析响应头）、不聚合金额、不筛行。
 *
 * ## 🔴 「不在导出范围内的内容」必须**逐条**列出 + 给原因（`AC-N4.11`）
 *
 * 数据来自**共享层常量** `BACKUP_EXCLUDED_TABLES` —— 与服务端过滤用的是**同一份**（一个来源）。
 * ⇒ 端侧**不写第二份文案**：哪天排除清单变了（如产品决定把 `migration_watermark` 也排除），
 * 这里**自动跟着变**，不会出现"接口排除了 4 张、页面还写着 3 张"。
 *
 * ## ⚠️ 不可静态断言的部分（诚实登记）
 *
 * 点击后的 `fetch → blob → <a download>` 三步需要真浏览器（基座是 `react-dom/server`，
 * 无 DOM —— 铁律 43）⇒ 单测只断言**渲染输入**（按钮 / 逐条清单 / 文案）；下载动作本身
 * 由无头取证或人工走查覆盖。见 `api/download.ts` 文件头。
 *
 * ## ⚠️ 本组件**不画**三样东西（与原型面板 3 的差异，均为"没有接口 / 刻意不开放"）
 *
 * - **自动备份**：PRD 明确 `AC-N4.32`「自动备份**不在 P0**（无该开关/入口）」⇒ **不画**（原型标 `P1 再露出`）；
 * - ✅ **覆盖前自动快照 / 可回滚**（`AC-N4.21`）：🔴 **已落地** —— 服务端批次 `67`（`DR-039`，新表
 *   `ledger_snapshots`）+ 端侧批次 `68`（本卡下方的「历史快照」区，组件 = `SnapshotSection`）；
 * - **导出历史**（"上次导出 9/01 · 3.2 MB"）：无接口 ⇒ 按「不画灰项」**不画**。
 *
 * 🔴 这条纪律是设置页一贯的「**不画灰项**」：一个点得动但什么都不发生的按钮，
 * 比没有这个按钮更糟 —— 它承诺了一件产品做不到的事（本页文件头有完整论述）。
 *
 * ✅ **`65` 批起已画**（此前挂"尚未落地"）：**从备份恢复**（`N4 §5.4.2`）——
 * 入口在本卡，全流程在 `RestoreDialog`（选文件 → 预览 → 逐项裁决 → 前置导出 + 二次输入 → 结果）。
 *
 * ✅ **`68` 批起已画**：**历史快照**（`AC-N4.21` · `DR-039`）—— 入口与列表在本卡
 * （`SnapshotSection`），回滚二次确认在 `RollbackConfirmDialog`。
 */

import { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, Divider, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { BACKUP_EXCLUDED_TABLES, ExportFormat, backupFileName, exportFileName, localDayOf } from '@jiaying/shared';
import { downloadAuthorized } from '../api/download.js';
import { listLedgerMembers } from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { useApp } from '../app/store.js';
import { EXPORT_BUSY_LABEL, EXPORT_FAILED_PREFIX, exportedNote } from '../features/export-labels.js';
import {
  BACKUP_ACTION,
  BACKUP_TITLE,
  CARD_NOTE,
  CSV_ACTION,
  CSV_ALL_NOTE,
  CSV_DESC,
  CSV_TITLE,
  DELETE_ACTION,
  DELETE_DESC,
  DELETE_TITLE,
  EXCLUDED_NOTE,
  EXCLUDED_TITLE,
  RESTORE_ACTION,
  RESTORE_DESC,
  RESTORE_OWNER_ONLY_NOTE,
  RESTORE_TITLE,
  SECTION_TITLE,
  backupDesc,
  excludedTag,
} from '../features/sovereignty-labels.js';
import { DeletionDialog } from './DeletionDialog.js';
import { RestoreDialog } from './RestoreDialog.js';
import { ScopeTag } from './ScopeTag.js';
import { SnapshotSection } from './SnapshotSection.js';

/** 档 1：建账以来**全部**流水（本页没有筛选上下文 ⇒ 显式 `exportAll`） */
const CSV_ALL_PATH = `/api/transactions/export?exportAll=true&format=${ExportFormat.Csv}`;
/** 档 2：全量业务表备份（批次 `61` 的接口） */
const BACKUP_PATH = '/api/settings/backup';

/** 端侧浏览器时区（只用于**兜底文件名**；首选是解析服务端 `Content-Disposition`） */
function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
  } catch {
    return 'UTC';
  }
}

/** 按钮的忙碌态（`null` = 都没有在导） */
type Busy = 'csv' | 'json' | null;

export function DataSovereigntyCard() {
  const { session } = useApp();
  const [busy, setBusy] = useState<Busy>(null);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** 🆕 删除账户对话框开关 + 「已提交」提示（`N4 §5.4.3`） */
  const [dlgOpen, setDlgOpen] = useState(false);
  const [deleteSubmitted, setDeleteSubmitted] = useState(false);
  /** 🆕 从备份恢复向导开关（`N4 §5.4.2` · 批次 `65`） */
  const [restoreOpen, setRestoreOpen] = useState(false);

  /**
   * 🔴 **owner 才可恢复** —— 与导出（全员可用）刻意不对称。
   *
   * ## 为什么判据取自**服务端成员名单**而不是"我是不是账本创建者"
   *
   * `role` 是服务端下发的权威字段（`GET /api/ledgers/members` · `AC-L6.1`：owner 与 member
   * 逐行相同）。端侧若自己推断"我 = 创建者"，在**转让所有权**之后会立刻判错 ——
   * 而转让恰恰是这个产品会发生的事。
   *
   * ## ⚠️ 这里**只是 UI 可见性**，不是权限
   *
   * 真正的闸在服务端（`@RequireOwner(PermissionAction.BulkOverwriteImport)`）——
   * 即便有人绕过 UI 直接发请求也拿不到数据。本判据的作用是**别让用户点一个注定失败的按钮**。
   */
  const isOwner = useQuery({
    queryKey: ['ledgerMembers'],
    queryFn: listLedgerMembers,
    select: (rows) => rows.find((m) => m.isSelf)?.role === 'owner',
  }).data === true;

  // 🔴 排除清单**直接来自共享层**（唯一的那个来源 —— 服务端过滤用的是同一份）
  const excluded = BACKUP_EXCLUDED_TABLES;

  const run = async (kind: Exclude<Busy, null>, path: string, fallback: string): Promise<void> => {
    setBusy(kind);
    setDone(null);
    setError(null);
    try {
      const fileName = await downloadAuthorized(path, fallback);
      setDone(fileName);
    } catch (e: unknown) {
      // 🔴 原样展示服务端 `message`（不自己编文案 —— 编了就是第二份口径，见 `http.ts` 文件头）
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  /**
   * 兜底文件名 —— 🔴 **调共享层同一对函数**（`exportFileName` / `backupFileName`），
   * 不在这里拼日期串。只在响应头不可用时才会用到。
   */
  const fallbackName = (kind: 'csv' | 'json'): string => {
    const today = localDayOf(Date.now(), browserTimeZone());
    const ledgerId = session?.ledgerId;
    return kind === 'csv'
      ? exportFileName(ExportFormat.Csv, today, ledgerId)
      : backupFileName(today, ledgerId);
  };

  return (
    <Card data-anchor={SECTION_TITLE} data-testid="sovereignty-card">
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {SECTION_TITLE}
          </Typography>
          <ScopeTag scope="账本级" />
        </Stack>
        <Typography variant="caption" color="text.secondary" component="div" data-testid="sovereignty-note">
          {CARD_NOTE}
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        {/* ── 档 1：导出流水（CSV）─ C 域实现 ── */}
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {CSV_TITLE}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {CSV_DESC}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {CSV_ALL_NOTE}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            disabled={busy !== null}
            data-testid="export-csv"
            onClick={() => {
              void run('csv', CSV_ALL_PATH, fallbackName('csv'));
            }}
          >
            {busy === 'csv' ? EXPORT_BUSY_LABEL : CSV_ACTION}
          </Button>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* ── 档 2：导出完整备份（JSON）─ N 域实现 ── */}
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {BACKUP_TITLE}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" data-testid="backup-desc">
              {backupDesc(excluded.length)}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            disabled={busy !== null}
            data-testid="export-json"
            onClick={() => {
              void run('json', BACKUP_PATH, fallbackName('json'));
            }}
          >
            {busy === 'json' ? EXPORT_BUSY_LABEL : BACKUP_ACTION}
          </Button>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* ── 🔴 不在导出范围内的内容（`AC-N4.11`：逐条明示 + 给原因）── */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {EXCLUDED_TITLE}
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={excludedTag(excluded.length)}
            data-testid="excluded-count"
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" component="div" data-testid="excluded-note">
          {EXCLUDED_NOTE}
        </Typography>
        <List dense disablePadding data-testid="excluded-list">
          {excluded.map((t) => (
            <ListItem key={t.name} disableGutters alignItems="flex-start" data-testid={`excluded-${t.name}`}>
              <ListItemText
                primary={
                  <Box component="code" sx={{ fontSize: '0.75rem' }}>
                    {t.name}
                  </Box>
                }
                secondary={t.reason}
              />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 1.5 }} />

        {/*
          🔴 **从备份恢复**（N4 §5.4.2 · 批次 `65` 端侧半边）——
          与导出**刻意不对称**：两条恢复路由都挂 `@RequireOwner(BulkOverwriteImport)`。
          ⇒ 非 owner 时**禁用而不是隐藏**（隐藏会让人以为"没这个功能"，禁用才说清"轮不到你"，
          与本页账本改名那处的处置一致）。
        */}
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {RESTORE_TITLE}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" data-testid="restore-desc">
              {RESTORE_DESC}
            </Typography>
            {!isOwner ? (
              <Typography variant="caption" color="text.secondary" component="div" data-testid="restore-owner-note">
                {RESTORE_OWNER_ONLY_NOTE}
              </Typography>
            ) : null}
          </Box>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            disabled={!isOwner || busy !== null}
            data-testid="restore-open"
            onClick={() => {
              setRestoreOpen(true);
            }}
          >
            {RESTORE_ACTION}
          </Button>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/*
          🔴 **历史快照**（`AC-N4.21` · `DR-039` D6 · 批次 `68` 端侧半边）——
          服务端三条路由（列表 / 下载 / 回滚）在批次 `67` 已落地并过真进程冒烟。
          ⚠️ 与上面「覆盖前不承诺可回滚」那条**不冲突**：那说的是"从备份恢复"的文案
             （`RESTORE_DESC`，它讲的是**上传外部备份**这条路径，兜底是强制前置导出）；
             本节讲的是**服务端自动落的那份快照**——两者是不同机制，各自如实陈述。
          🔴 `isOwner` 复用上面那份**服务端成员名单**判据（三条快照路由与两条恢复路由**同闸**）
        */}
        <SnapshotSection isOwner={isOwner} />

        <Divider sx={{ my: 1.5 }} />

        {/*
          🔴 **删除账户与全部数据**（N4 §5.4.3）—— 只给**入口**；确认流程（勾选已导出 + 二次输入
          账号名 + 四步说明）在 `DeletionDialog`。⚠️ 提交成功后账号即被冻结（`AuthGuard` 对
          `pending_deletion` 一律 403）⇒ 下一次请求会 401/403 把用户送回登录页，那里才是
          「冷静期剩余天数 + 撤销」的落点（`LoginPage`）。
        */}
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
              {DELETE_TITLE}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" data-testid="deletion-desc">
              {DELETE_DESC}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            color="error"
            data-testid="delete-account"
            onClick={() => {
              setDlgOpen(true);
            }}
          >
            {DELETE_ACTION}
          </Button>
        </Stack>
        {deleteSubmitted ? (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }} data-testid="deletion-submitted">
            已进入冷静期 —— 冷静期内账号冻结，剩余天数会在登录页显示，且随时可以撤销。
          </Typography>
        ) : null}
        <DeletionDialog
          open={dlgOpen}
          onClose={() => {
            setDlgOpen(false);
          }}
          accountName={session?.userName ?? ''}
          onSubmitted={() => {
            setDlgOpen(false);
            setDeleteSubmitted(true);
          }}
        />

        {/* 🔴 恢复向导（`N4 §5.4.2`）—— 全流程在对话框里；本卡只给入口 */}
        <RestoreDialog
          open={restoreOpen}
          onClose={() => {
            setRestoreOpen(false);
          }}
        />

        {/* ── 反馈 ── */}
        {done !== null ? (
          <Typography variant="caption" color="success.main" component="div" sx={{ mt: 1 }} data-testid="export-done">
            {exportedNote(done)}
          </Typography>
        ) : null}
        {error !== null ? (
          <Alert severity="error" sx={{ mt: 1 }} data-testid="export-error">
            {EXPORT_FAILED_PREFIX}
            {error}
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}
